/**
 * LocationSocialMediaService
 * Service for LocationSocialMedia entity operations
 * Route: api/location-social-media
 * Controller: LocationSocialMediaController.cs
 *
 * Composite key (MediaID + LocationID): this service deliberately does NOT extend CrudService, so the
 * single-id getById / update / delete that would hit the wrong routes do not exist.
 * Full rows carry credentials (Location.SocialMediaAccount.View); `getPublicSocialMedia` never does.
 * Omit `password` on update to keep the stored one.
 */
import { BaseService } from '../base/BaseService';
/**
 * LocationSocialMediaService
 * Handles location social media accounts by composite key
 * Maps to /api/location-social-media endpoint
 */
export class LocationSocialMediaService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? LocationSocialMediaService.DEFAULT_SERVICE_PATH,
            basePath: LocationSocialMediaService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * All location social accounts with credentials — Location.SocialMediaAccount.View
     * GET /api/location-social-media
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    getAllRaw() {
        return this.http.get(this.buildUrl());
    }
    /**
     * Get all social media accounts for a specific location (full details) — Location.SocialMediaAccount.View
     * GET /api/location-social-media/location/{locationId}
     * @param locationId - Location ID
     * @returns Array of location social media accounts
     */
    async getByLocationId(locationId) {
        const response = await this.getByLocationIdRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get all social media accounts for a specific location (raw)
     * @param locationId - Location ID
     * @returns ResponseData with array of location social media accounts
     */
    getByLocationIdRaw(locationId) {
        const endpoint = this.getEndpoint('byLocationId', `location/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Public social media for a location (handle, link, platform name/icon — never credentials)
     * GET /api/location-social-media/location/{locationId}/public
     * @param locationId - Location ID
     */
    async getPublicSocialMedia(locationId) {
        const response = await this.getPublicSocialMediaRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Public social media for a location (raw)
     * @param locationId - Location ID
     */
    getPublicSocialMediaRaw(locationId) {
        const endpoint = this.getEndpoint('publicSocialMedia', `location/${locationId}/public`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get a location social media by composite key (MediaID + LocationID) — Location.SocialMediaAccount.View
     * GET /api/location-social-media/{mediaId}/{locationId}
     * @param mediaId - Media ID
     * @param locationId - Location ID
     * @returns Location social media account
     */
    async getByCompositeKey(mediaId, locationId) {
        const response = await this.getByCompositeKeyRaw(mediaId, locationId);
        if (!response.data) {
            throw new Error(`Location social media (Media: ${mediaId}, Location: ${locationId}) not found`);
        }
        return response.data;
    }
    /**
     * Get a location social media by composite key (raw)
     */
    getByCompositeKeyRaw(mediaId, locationId) {
        const endpoint = this.getEndpoint('byCompositeKey', `${mediaId}/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Add a social account to a location (409 social_account_exists when the platform is already configured)
     * POST /api/location-social-media
     */
    async create(request) {
        const response = await this.createRaw(request);
        if (!response.data) {
            throw new Error('Failed to create location social media');
        }
        return response.data;
    }
    createRaw(request) {
        const endpoint = this.getEndpoint('create', '');
        return this.http.post(this.buildUrl(endpoint), request);
    }
    /**
     * Update a location social media account (omitted fields — incl. password — are kept)
     * PUT /api/location-social-media/{mediaId}/{locationId}
     * @param mediaId - Media ID
     * @param locationId - Location ID
     * @param request - Fields to change
     * @returns Updated location social media
     */
    async updateByCompositeKey(mediaId, locationId, request) {
        const response = await this.updateByCompositeKeyRaw(mediaId, locationId, request);
        if (!response.data) {
            throw new Error(`Failed to update location social media (Media: ${mediaId}, Location: ${locationId})`);
        }
        return response.data;
    }
    /**
     * Update a location social media account (raw)
     */
    updateByCompositeKeyRaw(mediaId, locationId, request) {
        const endpoint = this.getEndpoint('update', `${mediaId}/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, request);
    }
    /**
     * Delete a location social media account
     * DELETE /api/location-social-media/{mediaId}/{locationId}
     * @param mediaId - Media ID
     * @param locationId - Location ID
     * @returns True if deleted successfully
     */
    async deleteByCompositeKey(mediaId, locationId) {
        const response = await this.deleteByCompositeKeyRaw(mediaId, locationId);
        return response.success;
    }
    /**
     * Delete a location social media account (raw)
     */
    deleteByCompositeKeyRaw(mediaId, locationId) {
        const endpoint = this.getEndpoint('delete', `${mediaId}/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
}
LocationSocialMediaService.DEFAULT_SERVICE_PATH = 'locations';
LocationSocialMediaService.BASE_PATH = 'api/location-social-media';
//# sourceMappingURL=LocationSocialMediaService.js.map